import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge, ICON, cn } from '@attendance/ui';
import { stickyBelowToolbar } from '../../components/common/PageShell.js';
import type { SettingGroup } from './settingsCatalog.js';

/**
 * Where a group heading has to land so this bar is not sitting on it.
 *
 * Three stickies are stacked above the content: the app bar, `PageShell`'s
 * toolbar band, and this nav parked under both. A group anchored with
 * `scroll-mt-4` cleared 16px of that, so pressing a tab scrolled its heading
 * UNDERNEATH this strip — the reader got a form with a sliver of clipped text
 * where its title should be.
 *
 * A STYLE VALUE, not `scroll-mt-[calc(…)]`: the bracketed utility is what the
 * token gate's `arbitrary-space` rule bans, and the one other screen needing
 * this bought its way past with an allowlist entry — a list that may only
 * shrink. No literal value appears either way; every term is a measured var.
 */
export const SETTINGS_GROUP_SCROLL_MARGIN =
  'calc(var(--header-height) + var(--page-toolbar-h, 0px) + var(--settings-nav-h, 0px) + 1rem)';

/* ===========================================================================
 * The settings group strip — ten destinations, one of which is a URL.
 *
 * WHY IT IS STICKY, AND WHY THAT IS NOT A SECOND TOOLBAR.
 *
 * §2 of docs/design-rules.md gives a screen ONE toolbar, and this screen's is
 * the search bar in `PageShell`'s band. This is not a second one: it carries no
 * filter, no sort and no action — it is the page's own navigation, the thing
 * `AppShell`'s rail cannot draw because a settings group is not a rail row.
 *
 * It had to stick because of what sits under it. The pay group is 34 controls
 * across seven cards; scroll to the wage-file rule at the bottom of it and the
 * only way back to any other group is to scroll the whole thing back up. The
 * strip also carries the per-group unsaved counts, and "an edit on a tab you
 * are not looking at" — the one thing a tabbed form can lose — is exactly the
 * fact that must not scroll away.
 *
 * `stickyBelowToolbar` and never a literal `top`: the search band's height
 * depends on whether it wrapped, so the offset is 56px plus a MEASURED value
 * (§6 — "hardcode padding for chrome whose height depends on content" is the
 * anti-pattern). `z-20` matches `BulkActionBar`, the app's other second sticky,
 * and sits under the toolbar band's own `z-30`.
 *
 * `bg-background` is not decoration. A transparent sticky strip lets 34 field
 * rows scroll through its own text; the strip has to be opaque against the
 * page's ground, and the ground is what it is drawn on.
 *
 * WHY LINKS AND NOT `Tabs`. §6 says do not hand-roll a primitive you could
 * import, and the honest reading here is that `Tabs`/`Segmented` draw a
 * different object: a `<button role="tab">` inside a pill track, holding
 * component state. A settings group IS a route — "the pay rules" is a link you
 * send somebody — and a `<button>` cannot be middle-clicked, copied or opened
 * in a tab. So this stays a `<nav>` of real `<a href>`s. See the report note:
 * an underline strip is a third spelling of "pick one of N" in this app, and
 * the fix belongs in `packages/ui`, not in a private variant here.
 * ======================================================================== */
export function SettingsGroupNav({
  groups,
  activeGroupId,
  dirtyByGroup,
}: {
  groups: readonly SettingGroup[];
  activeGroupId: string | null;
  /** Unsaved edits per group id, so a count rides the tab it belongs to. */
  dirtyByGroup: ReadonlyMap<string, number>;
}): JSX.Element {
  const { t } = useTranslation();
  /* THIS BAR'S OWN HEIGHT, PUBLISHED — because a THIRD sticky moves where a
     heading has to land.
     `PageShell` measures the toolbar band into `--page-toolbar-h` so a second
     sticky can park under it. Settings puts THIS nav there, so a group heading
     scrolled to by a tab must clear the app bar, the toolbar AND this strip. It
     cleared 16px (`scroll-mt-4`), so pressing "Company" parked the word
     "Company" underneath this bar: the reader got a form with a sliver of
     clipped text where its heading should be.
     Measured, never written down — the strip wraps to two rows on a narrow
     viewport, so any constant is wrong at some width. Same reason
     `--page-toolbar-h` is measured. */
  const navRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const write = (): void =>
      document.documentElement.style.setProperty('--settings-nav-h', `${el.offsetHeight}px`);
    write();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(write);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--settings-nav-h');
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label={t('settings.sectionsNav')}
      className={cn(stickyBelowToolbar, 'z-20 -mt-element bg-background pt-element')}
    >
      <ul className="-mb-px flex gap-tight overflow-x-auto border-b border-border">
        {groups.map((group) => {
          const changed = dirtyByGroup.get(group.id);
          const active = group.id === activeGroupId;
          return (
            <li key={group.id} className="shrink-0">
              <Link
                to={`/settings/g/${group.id}`}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex min-h-touch items-center gap-control whitespace-nowrap border-b-2 px-3 py-2 text-copy font-medium transition-colors duration-fast ease-standard',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {group.pay && (
                  <AlertTriangle
                    size={ICON.inline}
                    aria-hidden="true"
                    className="shrink-0 text-warning"
                  />
                )}
                {t(`settings.g.${group.id}.title`)}
                {/* An edit on a tab you are not looking at is the one thing a
                    tabbed form can lose. The count rides on the tab so it is
                    visible from any other one — and, now that the strip is
                    sticky, from any scroll position too. */}
                {changed ? <Badge variant="primary">{changed}</Badge> : null}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
